/**
 * PipePage — `/pipe`. Дашборд «Клиенты & ПАЙП»: воронка продаж по выбранным
 * каналам + выполнение годового финплана. Данные — Bitrix24 (воронка КОММА),
 * GET /api/crm/pipe (см. server/src/crm/pipe.ts). Read-only.
 *
 * Порт эталонного Excel/PowerBI-дашборда Евгения: KPI-ряд, воронка, каналы,
 * WON по месяцам, статус квалификации, недельная динамика лидов и кумулятив
 * Выручки (План/Факт/Прогноз). По умолчанию выбраны 5 основных каналов
 * (2 сайта + КлубПервых + Прямые + Партнёрка), как и у Евгения.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { BaseChart } from '@/components/charts/BaseChart';
import { Badge, Icon, LoadingBlock, ErrorBlock } from '@/shared/ui';
import { chartTokens, baseChart, axisStyle } from '@/lib/echarts';
import {
  usePipe, useSavePipePlan,
  type FunnelStep, type ChannelRow, type MonthRow, type PipeDealRow, type ProductMonth, type PlanFactRow,
} from '@/hooks/api/usePipe';
import type { EChartsOption } from 'echarts';

/** «Основные» каналы для быстрой кнопки: 2 сайта + КлубПервых + Прямые + Партнёрка.
 *  По умолчанию (Игорь) выделяются ВСЕ каналы — см. эффект инициализации ниже. */
const MAIN_CHANNELS = ['1339', '1337', '1343', '1345', '1353'];
const CURRENT_YEAR = 2026;
const MONTHS_RU = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

function rub(v: number | null | undefined): string {
  if (v == null) return '—';
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(v);
}
function num(v: number | null | undefined): string {
  return v == null ? '—' : new Intl.NumberFormat('ru-RU').format(v);
}
function pctStr(v: number | null | undefined): string {
  return v == null ? '—' : `${v}%`.replace('.', ',');
}
function rubMln(v: number): string {
  return `${(v / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн`;
}
function shortDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function shortDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
}
/** Дебаунс значения — чтобы мультивыбор не дёргал API на каждый клик. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function PipePage() {
  usePageHeader({ title: 'Продажи', subtitle: 'Воронка продаж и выполнение финплана (Bitrix24)' });

  // Фильтры применяются сразу (Игорь: «без применить»), запрос к Bitrix —
  // с дебаунсом, чтобы мультивыбор не дёргал API на каждый клик. Кэш 2 мин.
  // По умолчанию выделены ВСЕ каналы (Игорь). Список каналов приходит с сервера,
  // поэтому первый набор проставляем эффектом, когда опции загрузились.
  const [channels, setChannels] = useState<string[]>([]);
  const channelsInit = useRef(false);
  const [months, setMonths] = useState<string[]>([]);
  // Выбранная неделя для раскрытия по дням (null → последняя неделя) + фильтр по месяцу.
  const [weekSel, setWeekSel] = useState<number | null>(null);
  const [monthSel, setMonthSel] = useState<string | null>(null);
  // Сортировка таблицы сделок по числовому столбцу (Евгений: «от большего к меньшему»).
  const [sortKey, setSortKey] = useState<'invoiceTotal' | 'expected' | 'weighted' | null>(null);
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const debChannels = useDebounced(channels, 400);
  const debMonths = useDebounced(months, 400);
  const { data, isLoading, isFetching, isError, error, refetch } = usePipe({
    year: CURRENT_YEAR,
    channels: debChannels,
    months: debMonths,
  });
  const scoped = data?.moneyScoped ?? false;
  const mLbl = (data?.filters?.months ?? []).join(', ');

  // Первичная инициализация: выделить ВСЕ каналы, как только опции пришли (Игорь).
  useEffect(() => {
    if (!channelsInit.current && (data?.channelOptions?.length ?? 0) > 0) {
      channelsInit.current = true;
      setChannels((data!.channelOptions!).map((o) => o.id));
    }
  }, [data]);

  const k = data?.kpis;
  const planDone = k?.planDonePct ?? 0;
  /** Кол-во по этапу воронки (шт), из data.funnel. */
  const funnelCount = (key: string): number => data?.funnel?.find((f) => f.key === key)?.count ?? 0;

  // Клик по заголовку столбца: тот же столбец — переключить направление, иначе — desc.
  const toggleSort = (key: 'invoiceTotal' | 'expected' | 'weighted') => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };
  const sortedDeals = useMemo(() => {
    const arr = [...(data?.deals ?? [])];
    if (!sortKey) return arr; // порядок с сервера (по счёту убыв.)
    const sign = sortDir === 'desc' ? -1 : 1;
    return arr.sort((a, b) => sign * ((a[sortKey] ?? 0) - (b[sortKey] ?? 0)));
  }, [data, sortKey, sortDir]);

  // План-факт выбранных месяцев (без фильтра — год) — в подвал таблицы сделок,
  // чтобы смотреть всё на одной строке и не скроллить к секции выше (Евгений 29.07).
  const pfFooter = useMemo(() => {
    const rows = data?.planFact ?? [];
    const sel = new Set(data?.filters?.months ?? []);
    const use = sel.size ? rows.filter((r) => sel.has(r.month)) : rows;
    const plan = use.reduce((s, r) => s + r.plan, 0);
    const fact = use.reduce((s, r) => s + r.fact, 0);
    return { plan, fact, delta: fact - plan };
  }, [data]);

  // Навигация по недельному графику: месяцы (сверху) + кнопки недель (снизу).
  const weeklyArr = data?.weekly ?? [];
  const weekMonthName = (iso: string): string => MONTHS_RU[Number(iso.slice(5, 7)) - 1] ?? '';
  const weekMonthList = useMemo(() => {
    const out: string[] = [];
    for (const w of weeklyArr) { const m = weekMonthName(w.week); if (m && !out.includes(m)) out.push(m); }
    return out;
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const selWeekIdx = weekSel != null && weekSel >= 0 && weekSel < weeklyArr.length ? weekSel : weeklyArr.length - 1;
  const weekBtns = weeklyArr
    .map((w, i) => ({ w, i }))
    .filter(({ w }) => !monthSel || weekMonthName(w.week) === monthSel);
  const pickMonth = (m: string) => {
    setMonthSel(m);
    const idxs = weeklyArr.map((w, i) => (weekMonthName(w.week) === m ? i : -1)).filter((i) => i >= 0);
    if (idxs.length) setWeekSel(idxs[idxs.length - 1]!); // последняя неделя месяца
  };

  const weeklyOption = useMemo<EChartsOption>(() => {
    const t = chartTokens();
    const w = data?.weekly ?? [];
    return {
      ...baseChart(t),
      tooltip: { trigger: 'axis' },
      legend: { data: ['Все сделки', 'MQL+'], top: 0, right: 0 },
      grid: { left: 44, right: 24, top: 28, bottom: 48, containLabel: true },
      xAxis: { type: 'category', data: w.map((p) => p.label), ...axisStyle(t), axisLabel: { ...axisStyle(t).axisLabel, interval: 0 } },
      yAxis: { type: 'value', name: 'Сделок', minInterval: 1, ...axisStyle(t) },
      series: [
        {
          name: 'Все сделки', type: 'line', smooth: false, symbol: 'circle', symbolSize: 7,
          data: w.map((p) => p.leads), lineStyle: { width: 2.5 }, color: t.primary,
          label: { show: true, position: 'top', fontSize: 11, color: t.textMuted },
        },
        {
          name: 'MQL+', type: 'line', smooth: false, symbol: 'circle', symbolSize: 6,
          data: w.map((p) => p.mqlPlus), lineStyle: { width: 2.5 }, color: t.success,
          label: { show: true, position: 'bottom', fontSize: 11, color: t.textMuted },
        },
      ],
    };
  }, [data]);

  const revenueOption = useMemo<EChartsOption>(() => {
    const t = chartTokens();
    const r = data?.revenueCumulative ?? [];
    const moneyFmt = (v: number) => rub(v);
    return {
      ...baseChart(t),
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v) => moneyFmt(Number(v)),
      },
      legend: { data: ['План', 'Факт', 'Прогноз'], top: 0, right: 0 },
      grid: { left: 56, right: 24, top: 28, bottom: 36, containLabel: true },
      xAxis: { type: 'category', data: r.map((p) => p.month), ...axisStyle(t) },
      yAxis: { type: 'value', name: '₽', ...axisStyle(t), axisLabel: { ...axisStyle(t).axisLabel, formatter: (v: number) => rubMln(v) } },
      series: [
        { name: 'План', type: 'line', smooth: false, showSymbol: false, data: r.map((p) => p.plan), lineStyle: { width: 2.5 }, color: t.primary },
        { name: 'Факт', type: 'line', smooth: false, showSymbol: false, data: r.map((p) => p.fact), lineStyle: { width: 3 }, color: t.success },
        { name: 'Прогноз', type: 'line', smooth: false, showSymbol: false, data: r.map((p) => p.forecast), lineStyle: { width: 2, type: 'dashed' }, color: t.warning },
      ],
    };
  }, [data]);

  const productOption = useMemo<EChartsOption>(() => {
    const t = chartTokens();
    const pm = data?.productMonths ?? [];
    const cats = data?.productCats ?? [];
    const palette = [t.primary, t.violet, t.warning, t.success, t.textFaint];
    return {
      ...baseChart(t),
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: (v) => rub(Number(v)) },
      legend: { data: cats, top: 0, right: 0 },
      grid: { left: 56, right: 24, top: 28, bottom: 36, containLabel: true },
      xAxis: { type: 'category', data: pm.map((m) => m.month), ...axisStyle(t) },
      yAxis: { type: 'value', name: '₽', ...axisStyle(t), axisLabel: { ...axisStyle(t).axisLabel, formatter: (v: number) => rubMln(v) } },
      series: cats.map((c, i) => ({
        name: c, type: 'bar', stack: 'дс', barMaxWidth: 46,
        data: pm.map((m) => Math.round(m.byCat[c] ?? 0)),
        color: palette[i % palette.length],
      })),
    };
  }, [data]);

  const maxFunnel = useMemo(() => Math.max(1, ...(data?.funnel ?? []).map((f) => f.count)), [data]);

  return (
    <div className="col gap20" style={{ maxWidth: 1280 }}>
      {/* Фильтры каналов + месяцев — мультивыбор, применяется сразу (с дебаунсом). */}
      <section className="card col" style={{ gap: 14 }}>
        <ChipMulti
          label="Каналы привлечения"
          options={data?.channelOptions ?? []}
          selected={channels}
          onChange={setChannels}
          extraActions={[{ label: 'Основные', onClick: () => setChannels(MAIN_CHANNELS) }]}
          emptyMeansAll
        />
        <div style={{ height: 1, background: 'var(--border)' }} />
        <ChipMulti
          label="Месяц (срок оплаты) — пересчитывает деньги"
          options={(data?.monthOptions ?? []).map((m) => ({ id: m, label: m }))}
          selected={months}
          onChange={setMonths}
          emptyMeansAll
        />
        <div className="row spread wrap" style={{ gap: 10, alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: 12 }}>
            Период: 01.01.{CURRENT_YEAR}—31.12.{CURRENT_YEAR}
            {data?.asOf && <> · <span className="mono" style={{ fontSize: 11.5 }}>обновлено {shortDateTime(data.asOf)}</span></>}
          </span>
          {isFetching && <span className="muted" style={{ fontSize: 12 }}>обновляю…</span>}
        </div>
      </section>

      {isLoading && <LoadingBlock label="Загружаю пайп из Bitrix24…" />}
      {isError && <ErrorBlock text={error?.message} onRetry={() => void refetch()} />}
      {data && data.configured === false && (
        <section className="card">
          <p className="muted" style={{ margin: 0 }}>CRM (Bitrix24) не подключён — нет данных пайпа.</p>
        </section>
      )}

      {!isLoading && !isError && data?.configured && k && (
        <>
          {/* KPI-ряд 1: количество (штуки) */}
          <div className="col" style={{ gap: 6 }}>
            <span className="muted" style={{ fontSize: 12, fontWeight: 700 }}>Количество, шт</span>
            <div className="stat-grid stat-grid--5">
              <Stat label="Новых сделок" value={num(k.newLeads)} />
              <Stat label="NQL" value={num(funnelCount('NQL'))} />
              <Stat label="MQL" value={num(funnelCount('MQL'))} />
              <Stat label="SQL" value={num(funnelCount('SQL'))} />
              <Stat label="WON" value={num(funnelCount('WON'))} tone="success" />
            </div>
          </div>

          {/* KPI-ряд 2: деньги / план (порядок по Игорю: план ₽ · факт ₽ · факт % · пайп всего · пайп взвешенный) */}
          <div className="col" style={{ gap: 6 }}>
            <span className="muted" style={{ fontSize: 12, fontWeight: 700 }}>Деньги / план{scoped ? ` · ${mLbl}` : ''}</span>
            <div className="stat-grid stat-grid--5">
              <Stat label="План, руб" value={rub(data.plan ?? 0)} />
              <Stat label={scoped ? `Факт, руб · ${mLbl}` : 'Факт, руб'} value={rub(k.won)} tone="success" delta="по оплаченным счетам" />
              <Stat label="Факт, %" value={pctStr(k.planDonePct)} tone={planDone >= 100 ? 'success' : 'neutral'} delta="от годового плана" />
              <Stat label={scoped ? `Пайп всего · ${mLbl}` : 'Пайп всего'} value={rub(k.pipe)} />
              <Stat label={scoped ? `Пайп взвешенный · ${mLbl}` : 'Пайп взвешенный'} value={rub(k.weighted)} />
            </div>
          </div>

          {/* Прогресс плана */}
          <section className="card col" style={{ gap: 8 }}>
            <div className="row spread">
              <h3 style={{ margin: 0 }}>Выполнение годового плана</h3>
              <span className="muted mono" style={{ fontSize: 12 }}>{rub(k.won)} / {rub(data.plan ?? 0)}</span>
            </div>
            <div style={{ height: 16, borderRadius: 999, background: 'var(--surface-alt)', border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, planDone)}%`, height: '100%', background: 'var(--success, #16A34A)', transition: 'width .4s' }} />
            </div>
          </section>

          {/* План-факт по месяцам: план — руками прямо здесь, факт — сам (Игорь/Евгений 28.07) */}
          <PlanFactSection
            year={CURRENT_YEAR}
            rows={data.planFact ?? []}
            totals={data.planFactTotals}
          />

          {/* Воронка + Каналы */}
          <div className="row wrap" style={{ gap: 20, alignItems: 'stretch' }}>
            <section className="card col" style={{ gap: 10, flex: '1 1 460px', minWidth: 360 }}>
              <div className="col" style={{ gap: 2 }}>
                <h3 style={{ margin: 0 }}>Основная воронка: количество и конверсии</h3>
                <span className="muted" style={{ fontSize: 11.5 }}>по всем сделкам — включая проваленные и выигранные (дальняя достигнутая стадия)</span>
              </div>
              <table className="tbl">
                <thead>
                  <tr><th>Этап</th><th style={{ textAlign: 'right' }}>Кол-во</th><th>Визуал</th><th style={{ textAlign: 'right' }}>Конв. к пред.</th><th style={{ textAlign: 'right' }}>Конв. от NQL</th></tr>
                </thead>
                <tbody>
                  {(data.funnel ?? []).map((f) => <FunnelRow key={f.key} f={f} max={maxFunnel} />)}
                </tbody>
              </table>
            </section>

            <section className="card col" style={{ gap: 10, flex: '1 1 460px', minWidth: 360 }}>
              <h3 style={{ margin: 0 }}>Каналы: компактная оценка качества</h3>
              <table className="tbl">
                <thead>
                  <tr><th>Канал</th><th style={{ textAlign: 'right' }}>NQL+</th><th style={{ textAlign: 'right' }}>WON, шт.</th><th style={{ textAlign: 'right' }}>Оплачено, ₽</th><th style={{ textAlign: 'right' }}>Средний чек</th><th style={{ textAlign: 'right' }}>NQL→WON</th></tr>
                </thead>
                <tbody>
                  {(data.channels ?? []).filter((c) => c.nqlPlus > 0).map((c) => <ChannelTableRow key={c.id} c={c} />)}
                  <ChannelTotals channels={data.channels ?? []} won={k.won} />
                </tbody>
              </table>
            </section>
          </div>

          {/* WON по месяцам + Статус квалификации */}
          <div className="row wrap" style={{ gap: 20, alignItems: 'stretch' }}>
            <section className="card col" style={{ gap: 10, flex: '1 1 520px', minWidth: 360 }}>
              <div className="col" style={{ gap: 2 }}>
                <h3 style={{ margin: 0 }}>Деньги по месяцам — по срокам оплаты счетов</h3>
                <span className="muted" style={{ fontSize: 11.5 }}>факт = счета в статусе «Оплачен»; неоплаченные счета (в т.ч. у WON-сделок) — в прогнозе</span>
              </div>
              <table className="tbl">
                <thead>
                  <tr><th>Месяц</th><th style={{ textAlign: 'right' }}>Оплачено, шт.</th><th style={{ textAlign: 'right' }}>Оплачено, ₽</th><th style={{ textAlign: 'right' }}>Средний чек</th><th style={{ textAlign: 'right' }}>Доля в факте</th><th style={{ textAlign: 'right' }}>Прогноз (счета)</th></tr>
                </thead>
                <tbody>
                  {(data.months ?? []).map((m) => <MonthTableRow key={m.month} m={m} />)}
                </tbody>
              </table>
            </section>

            <section className="card col" style={{ gap: 10, flex: '1 1 320px', minWidth: 280 }}>
              <h3 style={{ margin: 0 }}>Статус квалификации</h3>
              <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                {(data.qualification ?? []).map((q) => (
                  <Stat key={q.label} label={q.label} value={num(q.count)}
                    tone={q.label === 'Релевантный' ? 'success' : q.label === 'Не релевантный' ? 'danger' : 'neutral'} />
                ))}
              </div>
            </section>
          </div>

          {/* Недельная динамика лидов (по ВСЕМ каналам) + раскрытие по дням */}
          <section className="card">
            <div className="row spread" style={{ alignItems: 'baseline' }}>
              <h3 style={{ marginTop: 0 }}>Динамика поступления сделок по неделям</h3>
              <span className="muted" style={{ fontSize: 11.5 }}>все каналы · две кривые: все / MQL+</span>
            </div>
            {/* Месяцы сверху — быстрое переключение периода (Игорь/Саша) */}
            {weekMonthList.length > 0 && (
              <div className="row wrap" style={{ gap: 7, marginTop: 2 }}>
                <MiniChip active={!monthSel} onClick={() => setMonthSel(null)}>Все</MiniChip>
                {weekMonthList.map((m) => (
                  <MiniChip key={m} active={monthSel === m} onClick={() => pickMonth(m)}>{m}</MiniChip>
                ))}
              </div>
            )}
            <p className="muted" style={{ marginTop: 6, fontSize: 12 }}>неделя пн→вс (на выходных тоже капает) · с 06.04 (ранее — перенос из Excel, искажает масштаб)</p>
            <BaseChart
              option={weeklyOption}
              height={280}
              isEmpty={!weeklyArr.length}
              emptyText="Нет сделок"
              ariaLabel="Динамика по неделям"
              onEvents={{ click: (p) => { const i = (p as { dataIndex?: number })?.dataIndex; if (typeof i === 'number') setWeekSel(i); } }}
            />
            {/* Кнопки недель снизу — смотреть предыдущие недели, не только текущую */}
            {weekBtns.length > 0 && (
              <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
                <span className="muted" style={{ fontSize: 11.5, fontWeight: 700, alignSelf: 'center', marginRight: 2 }}>Неделя:</span>
                {weekBtns.map(({ w, i }) => (
                  <MiniChip key={w.week} active={i === selWeekIdx} onClick={() => setWeekSel(i)}>{w.label}</MiniChip>
                ))}
              </div>
            )}
            {weeklyArr.length > 0 && (() => {
              const wk = weeklyArr[selWeekIdx]!;
              return (
                <div className="col" style={{ gap: 6, marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                  <span className="muted" style={{ fontSize: 12, fontWeight: 700 }}>
                    Входящие сделки по дням · неделя с {wk.label} · всего {num(wk.leads)} · MQL+ {num(wk.mqlPlus)}
                  </span>
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {wk.days.map((d, j) => (
                      <div key={j} className="col" style={{ gap: 2, alignItems: 'center', minWidth: 62, padding: '8px 6px', borderRadius: 8, background: 'var(--surface-alt)' }}>
                        <span className="muted" style={{ fontSize: 11 }}>{d.label}</span>
                        <span className="tnum" style={{ fontSize: 18, fontWeight: 700, color: d.leads ? 'var(--primary)' : 'var(--text-muted)' }}>{d.leads}</span>
                        <span className="tnum" style={{ fontSize: 11, fontWeight: 700, color: d.mqlPlus ? 'var(--success-fg)' : 'var(--text-faint)' }}>MQL+ {d.mqlPlus}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </section>

          {/* Поступление ДС по продуктам и месяцам — составные столбики */}
          <section className="card">
            <div className="row spread" style={{ marginBottom: 4 }}>
              <h3 style={{ margin: 0 }}>Поступление ДС по продуктам и месяцам</h3>
              <span className="muted mono" style={{ fontSize: 12 }}>по срокам оплаты счетов · всего {rub(data.productTotals?.total)}</span>
            </div>
            <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
              когда и по каким продуктам придут деньги — для решений по расходам и закупкам
            </p>
            <BaseChart option={productOption} height={320}
              isEmpty={!(data.productMonths ?? []).length} emptyText="Нет счетов с продуктами"
              ariaLabel="Поступление ДС по продуктам" />
            {(data.productMonths ?? []).length > 0 && (
              <table className="tbl" style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Месяц</th>
                    {(data.productCats ?? []).map((c) => <th key={c} style={{ textAlign: 'right' }}>{c}</th>)}
                    <th style={{ textAlign: 'right' }}>Итого</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.productMonths ?? []).map((m) => (
                    <ProductMonthRow key={m.month} m={m} cats={data.productCats ?? []} />
                  ))}
                  <tr style={{ fontWeight: 800, background: 'var(--surface-alt)' }}>
                    <td>Итого</td>
                    {(data.productCats ?? []).map((c) => (
                      <td key={c} className="tnum" style={{ textAlign: 'right' }}>{rub(data.productTotals?.byCat[c] ?? 0)}</td>
                    ))}
                    <td className="tnum" style={{ textAlign: 'right' }}>{rub(data.productTotals?.total)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </section>

          {/* Выручка нарастающим итогом */}
          <section className="card">
            <h3 style={{ marginTop: 0 }}>Выручка {CURRENT_YEAR}: План vs Факт vs Прогноз (нарастающим итогом)</h3>
            <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
              план — месячные значения из «План-факта»; пока они не внесены — годовой план равными долями
            </p>
            <BaseChart option={revenueOption} height={320} isEmpty={!(data.revenueCumulative ?? []).length} emptyText="Нет данных" ariaLabel="Выручка нарастающим итогом" />
          </section>

          {/* Таблица сделок (открытые + выигранные), фильтр по полю «Месяц» */}
          <section className="card col" style={{ gap: 10 }}>
            <div className="row spread">
              <h3 style={{ margin: 0 }}>Сделки{scoped ? ` · ${mLbl}` : ''}</h3>
              <span className="muted mono" style={{ fontSize: 12 }}>
                {num((data.deals ?? []).length)} сделок · вкл. выигранные · ожидаемые = счета «отправлен клиенту» + «оплачен» (зелёным) · weighted = счёт × % по этапу
                {(data.dealsHidden ?? 0) > 0 && ` · скрыто ${num(data.dealsHidden)} пустых`}
              </span>
            </div>
            {/* Дубль месячного фильтра у самой таблицы — чтобы не скроллить
                наверх (Евгений 29.07). Тот же стейт, что у верхней плашки. */}
            <ChipMulti
              label="Месяц (срок оплаты) — пересчитывает деньги"
              options={(data.monthOptions ?? []).map((m) => ({ id: m, label: m }))}
              selected={months}
              onChange={setMonths}
              emptyMeansAll
            />
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 28 }} aria-label="Счета" /><th>Компания</th><th>Месяц</th><th>Этап воронки</th>
                  <SortTh label="Счёт" k="invoiceTotal" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortTh label="Ожидаемые деньги" k="expected" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortTh label="Weighted" k="weighted" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th>Последний комментарий</th>
                </tr>
              </thead>
              <tbody>
                {sortedDeals.map((d) => <DealRow key={d.id} d={d} />)}
                {(data.deals ?? []).length > 0 && (
                  <>
                    {/* «Всего» по ожидаемым — только неоплаченные («оранжевые»);
                        оплаченные — строкой ниже (Евгений 29.07). */}
                    <tr style={{ fontWeight: 800, background: 'var(--surface-alt)' }}>
                      <td /><td>Всего</td><td /><td />
                      <td className="tnum" style={{ textAlign: 'right' }}>{rub(data.dealsTotal?.invoiceMonth)}</td>
                      <td className="tnum" style={{ textAlign: 'right', color: 'var(--warning-fg, #B45309)' }}>{rub(data.dealsTotal?.expected)}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{rub(data.dealsTotal?.weighted)}</td>
                      <td />
                    </tr>
                    {/* Факт оплаченных денег отдельной строкой (Евгений 28.07). */}
                    <tr style={{ fontWeight: 700, background: 'var(--surface-alt)' }}>
                      <td /><td style={{ color: 'var(--success-fg)' }}>Оплачено (факт)</td><td /><td />
                      <td />
                      <td className="tnum" style={{ textAlign: 'right', color: 'var(--success-fg)' }}>{rub(data.dealsTotal?.expectedPaid ?? 0)}</td>
                      <td /><td />
                    </tr>
                    {/* План/Факт/Отклонение выбранного периода — всё на одной
                        странице, без скролла к секции план-факта (Евгений 29.07). */}
                    <tr style={{ fontWeight: 700, background: 'var(--surface-alt)' }}>
                      <td /><td>План-факт{scoped ? ` · ${mLbl}` : ' · год'}</td><td /><td />
                      <FooterStat label="план, ₽" value={rub(pfFooter.plan)} />
                      <FooterStat label="факт, ₽" value={rub(pfFooter.fact)} color="var(--success-fg)" />
                      <FooterStat
                        label="отклонение"
                        value={pfFooter.plan ? `${pfFooter.delta > 0 ? '+' : ''}${rub(pfFooter.delta)}` : '—'}
                        color={!pfFooter.plan ? undefined : pfFooter.delta >= 0 ? 'var(--success-fg)' : 'var(--danger-fg)'}
                      />
                      <td />
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}

function FunnelRow({ f, max }: { f: FunnelStep; max: number }) {
  return (
    <tr>
      <td style={{ fontWeight: 700 }}>{f.label}</td>
      <td className="tnum" style={{ textAlign: 'right', fontWeight: 700 }}>{num(f.count)}</td>
      <td style={{ width: 160 }}>
        <div style={{ height: 14, borderRadius: 4, background: 'var(--surface-alt)', overflow: 'hidden' }}>
          <div style={{ width: `${(f.count / max) * 100}%`, height: '100%', background: 'var(--primary)' }} />
        </div>
      </td>
      <td className="tnum muted" style={{ textAlign: 'right' }}>{f.key === 'NQL' ? '—' : pctStr(f.convPrev)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{pctStr(f.convNql)}</td>
    </tr>
  );
}

function ChannelTableRow({ c }: { c: ChannelRow }) {
  return (
    <tr>
      <td>{c.label}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{num(c.nqlPlus)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{num(c.wonCount)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{rub(c.won)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{rub(c.avgCheck)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{pctStr(c.nqlToWon)}</td>
    </tr>
  );
}

function ChannelTotals({ channels, won }: { channels: ChannelRow[]; won: number }) {
  const nql = channels.reduce((a, c) => a + c.nqlPlus, 0);
  const wc = channels.reduce((a, c) => a + c.wonCount, 0);
  const avg = wc ? Math.round(won / wc) : null;
  return (
    <tr style={{ fontWeight: 800, background: 'var(--surface-alt)' }}>
      <td>Итого</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{num(nql)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{num(wc)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{rub(won)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{rub(avg)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{pctStr(nql ? Math.round((wc / nql) * 1000) / 10 : null)}</td>
    </tr>
  );
}

function MonthTableRow({ m }: { m: MonthRow }) {
  return (
    <tr>
      <td style={{ fontWeight: 600 }}>{m.month}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{num(m.wonCount)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{rub(m.won)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{rub(m.avgCheck)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{pctStr(m.shareWon)}</td>
      <td className="tnum muted" style={{ textAlign: 'right' }}>{m.forecast ? rub(m.forecast) : '—'}</td>
    </tr>
  );
}

/** План-факт по месяцам (Игорь 28.07: «мы планируем, а какой факт получаем — не
 *  видно»). План — ручной ввод кликом по ячейке (сохраняется в БД, к Б24 не
 *  привязан). Факт — оплаченные счета по всем каналам, фильтры не влияют. */
function PlanFactSection({ year, rows, totals }: {
  year: number;
  rows: PlanFactRow[];
  totals?: { plan: number; fact: number; delta: number; donePct: number | null };
}) {
  const save = useSavePipePlan();
  const nowIdx = new Date().getFullYear() === year ? new Date().getMonth() : -1;
  if (!rows.length) return null;
  return (
    <section className="card col" style={{ gap: 10 }}>
      <div className="row spread wrap" style={{ gap: 8, alignItems: 'baseline' }}>
        <h3 style={{ margin: 0 }}>План-факт по месяцам · {year}</h3>
        <span className="muted" style={{ fontSize: 11.5 }}>
          план вводится вручную — клик по ячейке «План» · факт = оплаченные счета по всем каналам (фильтры не влияют)
          {save.isPending && ' · сохраняю…'}
        </span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Месяц</th>
            <th style={{ textAlign: 'right' }}>План, ₽</th>
            <th style={{ textAlign: 'right' }}>Факт, ₽</th>
            <th style={{ textAlign: 'right' }}>Отклонение</th>
            <th style={{ textAlign: 'right' }}>Выполнение</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.month} style={r.monthIdx === nowIdx ? { background: 'var(--surface-alt)' } : undefined}>
              <td style={{ fontWeight: r.monthIdx === nowIdx ? 800 : 600 }}>
                {r.month}{r.monthIdx === nowIdx && <span className="muted" style={{ fontSize: 10.5 }}> · текущий</span>}
              </td>
              <PlanCell
                value={r.plan}
                onSave={(amount) => save.mutate({ year, month: r.monthIdx + 1, amount })}
              />
              <td className="tnum" style={{ textAlign: 'right', fontWeight: 600, color: r.fact ? 'var(--success-fg)' : undefined }}>
                {r.fact ? rub(r.fact) : '—'}
              </td>
              <td className="tnum" style={{ textAlign: 'right', color: !r.plan ? undefined : r.delta >= 0 ? 'var(--success-fg)' : 'var(--danger-fg)' }}>
                {r.plan ? `${r.delta > 0 ? '+' : ''}${rub(r.delta)}` : '—'}
              </td>
              <td className="tnum" style={{ textAlign: 'right', fontWeight: 700, color: r.donePct != null && r.donePct >= 100 ? 'var(--success-fg)' : undefined }}>
                {pctStr(r.donePct)}
              </td>
            </tr>
          ))}
          {totals && (
            <tr style={{ fontWeight: 800, background: 'var(--surface-alt)' }}>
              <td>Итого</td>
              <td className="tnum" style={{ textAlign: 'right' }}>{rub(totals.plan)}</td>
              <td className="tnum" style={{ textAlign: 'right' }}>{rub(totals.fact)}</td>
              <td className="tnum" style={{ textAlign: 'right', color: !totals.plan ? undefined : totals.delta >= 0 ? 'var(--success-fg)' : 'var(--danger-fg)' }}>
                {totals.plan ? `${totals.delta > 0 ? '+' : ''}${rub(totals.delta)}` : '—'}
              </td>
              <td className="tnum" style={{ textAlign: 'right' }}>{pctStr(totals.donePct)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

/** Редактируемая ячейка «План»: клик → инпут, Enter/blur — сохранить, Esc — отмена. */
function PlanCell({ value, onSave }: { value: number; onSave: (amount: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const start = () => { setText(value ? String(value) : ''); setEditing(true); };
  const commit = () => {
    setEditing(false);
    const amount = Number(text.replace(/[^\d]/g, '')) || 0;
    if (amount !== value) onSave(amount);
  };
  if (editing) {
    return (
      <td style={{ textAlign: 'right' }}>
        <input
          autoFocus
          inputMode="numeric"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          style={{
            width: 130, textAlign: 'right', fontSize: 13, fontWeight: 600,
            padding: '4px 8px', borderRadius: 6,
            border: '1px solid var(--primary)', background: 'var(--surface)', color: 'var(--text)',
          }}
          aria-label="План месяца, ₽"
        />
      </td>
    );
  }
  return (
    <td
      className="tnum"
      onClick={start}
      title="Кликните, чтобы ввести план месяца"
      style={{ textAlign: 'right', fontWeight: 600, cursor: 'pointer' }}
    >
      {value ? rub(value) : <span style={{ color: 'var(--primary)', fontWeight: 600, fontSize: 12 }}>+ ввести</span>}
    </td>
  );
}

function ProductMonthRow({ m, cats }: { m: ProductMonth; cats: string[] }) {
  const [open, setOpen] = useState(false);
  const hasItems = m.items.length > 0;
  return (
    <>
      <tr style={hasItems ? { cursor: 'pointer' } : undefined} onClick={hasItems ? () => setOpen((v) => !v) : undefined}>
        <td style={{ fontWeight: 600 }}>
          <span className="row center" style={{ gap: 6 }}>
            {hasItems && <Icon name={open ? 'ChevronDown' : 'ChevronRight'} size={14} style={{ color: 'var(--text-muted)' }} />}
            {m.month}
          </span>
        </td>
        {cats.map((c) => (
          <td key={c} className="tnum" style={{ textAlign: 'right' }}>{m.byCat[c] ? rub(m.byCat[c]) : '—'}</td>
        ))}
        <td className="tnum" style={{ textAlign: 'right', fontWeight: 700 }}>{rub(m.total)}</td>
      </tr>
      {open && hasItems && (
        <tr>
          <td colSpan={cats.length + 2} style={{ paddingTop: 0 }}>
            <div className="col" style={{ gap: 4, padding: '4px 0 8px 24px' }}>
              <span className="muted" style={{ fontSize: 11, fontWeight: 700 }}>Заказчики ({m.month}):</span>
              {m.items.map((it, i) => (
                <div key={i} className="row" style={{ gap: 10, alignItems: 'center', fontSize: 12 }}>
                  <span style={{ fontWeight: 600, minWidth: 200 }}>{it.company.split('\n')[0]}</span>
                  <Badge tone="b-neutral">{it.cat}</Badge>
                  <span className="muted" style={{ flex: 1, minWidth: 120 }}>{it.product || '—'}</span>
                  <span className="tnum" style={{ fontWeight: 600, minWidth: 110, textAlign: 'right' }}>{rub(it.amount)}</span>
                  <span className="muted mono" style={{ fontSize: 10.5, minWidth: 80, textAlign: 'right' }}>срок {shortDate(it.dueDate)}</span>
                  {it.paid && <Badge tone="b-success">оплачен</Badge>}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DealRow({ d }: { d: PipeDealRow }) {
  const [open, setOpen] = useState(false);
  const hasInv = d.invoices.length > 0;
  return (
    <>
      <tr style={hasInv ? { cursor: 'pointer' } : undefined} onClick={hasInv ? () => setOpen((v) => !v) : undefined}>
        <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          {hasInv && <Icon name={open ? 'ChevronDown' : 'ChevronRight'} size={15} />}
        </td>
        <td style={{ maxWidth: 220 }}>
          <span style={{ fontWeight: 600, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {d.company.split('\n')[0]}
          </span>
          {d.splitNote ? (
            <span className="muted" style={{ fontSize: 10.5 }}>{d.splitNote}</span>
          ) : d.status ? (
            <span className="muted" style={{ fontSize: 11 }}>{d.status}</span>
          ) : null}
        </td>
        <td>
          <Badge tone="b-neutral">{d.month}</Badge>
          {/* Клиент планировался в одном месяце, деньги пришли в другом (Евгений 28.07). */}
          {d.movedNote && (
            <span style={{ display: 'block', fontSize: 10.5, color: 'var(--warning-fg, #B45309)', whiteSpace: 'nowrap' }}>
              {d.movedNote}
            </span>
          )}
        </td>
        <td className="muted" style={{ fontSize: 12 }}>{d.stageName}</td>
        <td className="tnum" style={{ textAlign: 'right', fontWeight: 600 }}>{d.invoiceTotal ? rub(d.invoiceTotal) : '—'}</td>
        {/* Зелёный — вся сумма оплачена; оранжевый — ждём оплату; при частичной
            оплате — подпись «оплачено N» под суммой. */}
        <td className="tnum" style={{ textAlign: 'right', fontWeight: 600 }}>
          {d.expected ? (
            <>
              <span style={{ color: d.expectedPaid >= d.expected ? 'var(--success-fg)' : 'var(--warning-fg, #B45309)' }}>
                {rub(d.expected)}
              </span>
              {d.expectedPaid > 0 && d.expectedPaid < d.expected && (
                <span style={{ display: 'block', fontSize: 10.5, color: 'var(--success-fg)' }}>оплачено {rub(d.expectedPaid)}</span>
              )}
            </>
          ) : '—'}
        </td>
        <td className="tnum" style={{ textAlign: 'right' }}>{d.weighted ? rub(d.weighted) : '—'}</td>
        <td className="muted" style={{ fontSize: 11.5, maxWidth: 280 }}>
          {d.comment ? (
            <span title={d.comment} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{d.comment}</span>
          ) : '—'}
        </td>
      </tr>
      {open && hasInv && (
        <tr>
          <td />
          <td colSpan={7} style={{ paddingTop: 0 }}>
            <div className="col" style={{ gap: 4, padding: '4px 0 8px' }}>
              <span className="muted" style={{ fontSize: 11, fontWeight: 700 }}>График оплат (счета):</span>
              {d.invoices.map((inv, i) => (
                <div key={i} className="row" style={{ gap: 10, alignItems: 'center', fontSize: 12 }}>
                  <Badge tone={inv.paid ? 'b-success' : 'b-neutral'}>{inv.month}</Badge>
                  <span className="tnum" style={{ fontWeight: 600, minWidth: 96, textAlign: 'right' }}>{rub(inv.amount)}</span>
                  <span className="muted">{inv.name || 'счёт'}</span>
                  <span className="muted mono" style={{ fontSize: 10.5 }}>срок {shortDate(inv.dueDate)}</span>
                  {inv.paid && <Badge tone="b-success">оплачен</Badge>}
                  {inv.sent && <Badge tone="b-warning">отправлен клиенту</Badge>}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Ячейка подвала таблицы сделок: значение + мелкая подпись (план/факт/откл.). */
function FooterStat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <td className="tnum" style={{ textAlign: 'right' }}>
      <span style={{ color }}>{value}</span>
      <span className="muted" style={{ display: 'block', fontSize: 9.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</span>
    </td>
  );
}

/** Кликабельный заголовок столбца таблицы сделок — сортировка по убыв./возр. */
function SortTh({ label, k, sortKey, sortDir, onSort }: {
  label: string;
  k: 'invoiceTotal' | 'expected' | 'weighted';
  sortKey: string | null;
  sortDir: 'desc' | 'asc';
  onSort: (k: 'invoiceTotal' | 'expected' | 'weighted') => void;
}) {
  const active = sortKey === k;
  const arrow = !active ? '↕' : sortDir === 'desc' ? '↓' : '↑';
  return (
    <th
      onClick={() => onSort(k)}
      aria-sort={active ? (sortDir === 'desc' ? 'descending' : 'ascending') : 'none'}
      style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      title="Сортировать (клик — по убыванию, ещё раз — по возрастанию)"
    >
      {label}{' '}
      <span style={{ color: active ? 'var(--primary)' : 'var(--text-faint)', fontWeight: 700 }}>{arrow}</span>
    </th>
  );
}

/** Компактный чип-переключатель (месяцы/недели в графике динамики). */
function MiniChip({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      style={{
        cursor: 'pointer', fontSize: 12, padding: '4px 10px', borderRadius: 999,
        border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
        background: active ? 'var(--primary)' : 'var(--surface-alt)',
        color: active ? 'var(--on-primary, #fff)' : 'var(--text-muted)',
        fontWeight: active ? 700 : 500, transition: 'background .12s, border-color .12s',
      }}
    >
      {children}
    </button>
  );
}

/** Текстовая кнопка-ссылка (действия фильтра). */
function LinkBtn({ onClick, disabled, children }: {
  onClick: () => void; disabled?: boolean; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        border: 'none', background: 'none', padding: 0, fontSize: 12, fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'var(--text-faint, #9CA3AF)' : 'var(--primary)',
      }}
    >
      {children}
    </button>
  );
}

/** Мультивыбор чипами: выбрать несколько, «Все»/«Сбросить» + доп.действия. */
function ChipMulti({
  label, options, selected, onChange, extraActions, emptyMeansAll,
}: {
  label: string;
  options: { id: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  extraActions?: { label: string; onClick: () => void }[];
  emptyMeansAll?: boolean;
}) {
  const allOn = options.length > 0 && selected.length === options.length;
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="col" style={{ gap: 8 }}>
      <div className="row spread wrap" style={{ gap: 8, alignItems: 'baseline' }}>
        <span className="muted" style={{ fontSize: 12, fontWeight: 700 }}>
          {label}
          <span style={{ fontWeight: 500 }}> · выбрано {selected.length}{allOn ? ' (все)' : ''}</span>
        </span>
        <div className="row" style={{ gap: 12 }}>
          <LinkBtn onClick={() => onChange(options.map((o) => o.id))} disabled={allOn}>Выбрать все</LinkBtn>
          <LinkBtn onClick={() => onChange([])} disabled={selected.length === 0}>Сбросить</LinkBtn>
          {extraActions?.map((a) => (
            <LinkBtn key={a.label} onClick={a.onClick}>{a.label}</LinkBtn>
          ))}
        </div>
      </div>
      <div className="row wrap" style={{ gap: 7 }}>
        {options.map((o) => {
          const on = selected.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(o.id)}
              className="row center"
              style={{
                cursor: 'pointer', fontSize: 12.5, padding: '5px 12px', borderRadius: 999, gap: 5,
                border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                background: on ? 'var(--primary)' : 'var(--surface-alt)',
                color: on ? 'var(--on-primary, #fff)' : 'var(--text-muted)',
                fontWeight: on ? 700 : 500, transition: 'background .12s, border-color .12s',
              }}
            >
              {on && <Icon name="Check" size={13} />}
              {o.label}
            </button>
          );
        })}
      </div>
      {emptyMeansAll && selected.length === 0 && (
        <span className="muted" style={{ fontSize: 11.5 }}>ничего не выбрано — учитываются все</span>
      )}
    </div>
  );
}

function Stat({ label, value, delta, tone = 'neutral' }: {
  label: string; value: string; delta?: string; tone?: 'success' | 'danger' | 'neutral';
}) {
  const deltaColor = tone === 'success' ? 'var(--success-fg)' : tone === 'danger' ? 'var(--danger-fg)' : 'var(--text-muted)';
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={{ color: tone === 'success' ? 'var(--success-fg)' : undefined }}>{value}</div>
      {delta && <div className="stat__delta" style={{ color: deltaColor }}>{delta}</div>}
    </div>
  );
}
